import { useEffect, useState } from "react";
import { X, BookMarked, Plus, Trash2, Check, RotateCcw, FileDown, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API, openDownload } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const RES_TYPES = [
  { key: "fiber", label: "Włókno", coll: "cables", detail: "Nr włókna" },
  { key: "splitter_port", label: "Port splittera", coll: "splitters", detail: "Nr portu" },
  { key: "microduct", label: "Mikrorurka", coll: "routes", detail: "Nr mikrorurki" },
  { key: "pon_port", label: "Port PON", coll: "olt", detail: "Nr portu PON" },
];

const STATUS_STYLE = {
  "aktywna": "bg-amber-500/15 text-amber-300 border-amber-500/50",
  "wygasła": "bg-red-500/15 text-red-300 border-red-500/50",
  "zwolniona": "bg-slate-500/15 text-slate-300 border-slate-500/50",
  "zrealizowana": "bg-green-500/15 text-green-300 border-green-500/50",
};

export default function ReservationsPanel({ role, onClose }) {
  const { t } = useI18n();
  const canEdit = role !== "odczyt";
  const canDelete = role === "admin" || role === "projektant";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [objects, setObjects] = useState([]);
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ resource_type: "fiber", resource_id: "", detail: "", reserved_for: "", expires_at: "", note: "" });

  const typeCfg = RES_TYPES.find((r) => r.key === form.resource_type) || RES_TYPES[0];

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/reservations"); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/${typeCfg.coll}`);
      setObjects(data || []);
    })();
    setForm((f) => ({ ...f, resource_id: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.resource_type]);

  const create = async () => {
    if (!form.resource_id) return toast.error(t("Wybierz zasób do rezerwacji"));
    setBusy(true);
    try {
      await api.post("/reservations", form);
      toast.success(t("Rezerwacja utworzona"));
      setForm({ resource_type: form.resource_type, resource_id: "", detail: "", reserved_for: "", expires_at: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (id, kind) => {
    try {
      await api.post(`/reservations/${id}/${kind}`);
      toast.success(kind === "release" ? t("Rezerwacja zwolniona") : t("Rezerwacja zrealizowana"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/reservations/${id}`); toast.success(t("Usunięto")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const dl = (path) => {
    openDownload(`/reservations/${path}`);
  };

  const objLabel = (o) => o.number || o.name || o.identyfikator || o.id;
  const filtered = rows.filter((r) => (fType === "all" || r.resource_type === fType) && (fStatus === "all" || r.status === fStatus));

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="reservations-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2"><BookMarked size={18} className="text-amber-400" />
          <h2 className="font-heading font-bold text-lg">{t("Rezerwacje zasobów")}</h2></div>
        <div className="flex items-center gap-2">
          <button onClick={() => dl("report.pdf")} data-testid="res-pdf" title={t("Raport PDF")}
            className="p-1.5 rounded hover:bg-slate-700 text-red-300"><FileDown size={17} /></button>
          <button onClick={() => dl("export")} data-testid="res-csv" title={t("Eksport CSV")}
            className="p-1.5 rounded hover:bg-slate-700 text-green-300"><FileSpreadsheet size={17} /></button>
          <button onClick={onClose} data-testid="reservations-close" className="p-1.5 rounded hover:bg-slate-700"><X size={18} /></button>
        </div>
      </div>

      <div className="overflow-y-auto p-5 space-y-5">
        {canEdit && (
          <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700">
            <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><Plus size={16} className="text-amber-400" /> {t("Nowa rezerwacja")}</h3>
            <div className="grid grid-cols-2 gap-2">
              <select value={form.resource_type} onChange={(e) => setForm({ ...form, resource_type: e.target.value })}
                data-testid="res-type" className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm">
                {RES_TYPES.map((r) => <option key={r.key} value={r.key}>{t(r.label)}</option>)}
              </select>
              <select value={form.resource_id} onChange={(e) => setForm({ ...form, resource_id: e.target.value })}
                data-testid="res-object" className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm">
                <option value="">{t("— wybierz zasób —")}</option>
                {objects.map((o) => <option key={o.id} value={o.id}>{objLabel(o)}</option>)}
              </select>
              <input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} placeholder={t(typeCfg.detail)}
                data-testid="res-detail" className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm" />
              <input value={form.reserved_for} onChange={(e) => setForm({ ...form, reserved_for: e.target.value })} placeholder={t("Zarezerwowano dla (klient/projekt)")}
                data-testid="res-for" className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm" />
              <div>
                <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Data wygaśnięcia (opcjonalnie)")}</label>
                <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  data-testid="res-expires" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm" />
              </div>
              <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("Notatka")}
                data-testid="res-note" className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm self-end" />
            </div>
            <button onClick={create} disabled={busy} data-testid="res-create"
              className="mt-3 w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-60 rounded py-2 text-sm flex items-center justify-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <BookMarked size={15} />} {t("Zarezerwuj")}</button>
          </div>
        )}

        <div className="flex items-center gap-2 text-xs">
          <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="res-filter-type"
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5">
            <option value="all">{t("Wszystkie typy")}</option>
            {RES_TYPES.map((r) => <option key={r.key} value={r.key}>{t(r.label)}</option>)}
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="res-filter-status"
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5">
            <option value="all">{t("Wszystkie statusy")}</option>
            {["aktywna", "wygasła", "zwolniona", "zrealizowana"].map((s) => <option key={s} value={s}>{t(s)}</option>)}
          </select>
          <span className="ml-auto text-slate-500 font-mono">{filtered.length}</span>
        </div>

        {loading ? <div className="text-slate-500 text-sm">{t("Ładowanie…")}</div> : filtered.length === 0 ? (
          <div className="text-slate-500 text-sm text-center py-8">{t("Brak rezerwacji")}</div>
        ) : (
          <div className="space-y-2" data-testid="res-list">
            {filtered.map((r) => (
              <div key={r.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3" data-testid={`res-row-${r.id}`}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wide text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5">{t(r.type_label)}</span>
                      <span className="font-semibold text-slate-200">{r.resource_label || "—"}{r.detail ? ` · ${r.detail}` : ""}</span>
                      <span className={`text-[10px] rounded px-1.5 py-0.5 border ${STATUS_STYLE[r.status] || ""}`}>{t(r.status)}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {t("Dla:")} <b className="text-slate-300">{r.reserved_for || "—"}</b>
                      {r.expires_at ? <> · {t("wygasa:")} {String(r.expires_at).slice(0, 10)}</> : null}
                      {r.created_by_name ? <> · {r.created_by_name}</> : null}
                    </div>
                    {r.note ? <div className="text-[11px] text-slate-500 mt-0.5">{r.note}</div> : null}
                  </div>
                  {canEdit && (r.status === "aktywna" || r.status === "wygasła") && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => act(r.id, "fulfill")} data-testid={`res-fulfill-${r.id}`} title={t("Zrealizuj")}
                        className="p-1.5 rounded hover:bg-green-700/40 text-green-400"><Check size={15} /></button>
                      <button onClick={() => act(r.id, "release")} data-testid={`res-release-${r.id}`} title={t("Zwolnij")}
                        className="p-1.5 rounded hover:bg-slate-700 text-slate-300"><RotateCcw size={15} /></button>
                    </div>
                  )}
                  {canDelete && (
                    <button onClick={() => del(r.id)} data-testid={`res-delete-${r.id}`} title={t("Usuń")}
                      className="p-1.5 rounded hover:bg-red-900/40 text-red-400 shrink-0"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
